"use client";
import React, { useMemo, useState } from "react";
import { BranchSummary } from "../../constants/branchData";
import HeroPage from "./components/HeroPage";
import Skeleton from "react-loading-skeleton";
import CardGrid from "../../CardGrid";
import { TeamCard } from "./components/TeamCard";
import { ShowMoreText } from "./About1";
import Image from "next/image";
import CustomSelect from "./components/ui/form/CustomSelect";
import { MemberType } from "src/types";
import { BranchType } from "./components";
import { PropertyCardSkeleton } from "../design2/components/Skeletons/Design2PropertyCard";
import { areAllValuesZero } from "./Staff";
import { useBranchSelection } from "src/app/hooks/useBranchesLogic";
const Branches1 = ({ initialData }: { initialData: BranchType }) => {
  const { selectedValue, branchOptions, branchDetails, loading, handleSelect } =
    useBranchSelection(initialData);
  return (
    <section className="w-full max-w-full overflow-x-clip">
      <HeroPage
        subHeader="Meet our dedicated team, committed to delivering exceptional real estate service and expertise."
        header="Branches & Staffs"
      />
      <div className="wrapper  pt-10">
        <div className="title-filter mb-5 flex justify-between">
          <h2 className="text-primary font-medium">
            Company Branches{" "}
            {!loading && !branchDetails && (
              <p className="font-normal text-lg">
                We’re currently updating our branch details. Soon, you’ll be
                able to view the locations, contact information, and services
                offered by our various branches across the region. Please check
                back shortly for the latest updates.
              </p>
            )}
          </h2>
          {!loading && !branchDetails ? null : (
            <div className="filter rounded-md h-max p-2 border border-themeColor">
              <CustomSelect
                options={branchOptions}
                value={
                  branchOptions.find((option) => option.value === selectedValue)
                    ?.value as string
                }
                onChange={handleSelect}
                placeholder="Select a Branch"
              />
            </div>
          )}
        </div>
        {loading || branchDetails ? (
          <>
            <div className="grid-container xs:mb-4 mb-8">
              <FirstCol
                reloading={loading}
                loading={loading}
                address={branchDetails?.address}
                name={branchDetails?.branchName}
                image={branchDetails?.picture}
              />
              {!loading &&
              areAllValuesZero(branchDetails?.branchSummary?.values) ? null : (
                <SecondCol
                  loading={loading}
                  branchDetails={branchDetails as BranchType}
                />
              )}
            </div>
            <div
              style={{
                boxShadow: "0px 2px 10px 0px rgba(33, 33, 33, 0.05)",
              }}
              className="about text-secondary-main bg-white p-4 rounded-xl shadow-xl"
            >
              <div className="mb-4 pb-3 text-xl border-b border-b-disabled-main font-medium">
                About Branch
              </div>
              <div className="text-secondary-main flex-col gap-4">
                <ShowMoreText
                  loading={loading}
                  text={
                    branchDetails?.description || "No information available"
                  }
                />
              </div>
            </div>
          </>
        ) : null}
        <FourthCol loading={loading} data={branchDetails?.staffs} />
      </div>
    </section>
  );
};
export default Branches1;

const FirstCol: React.FC<{
  image?: string;
  name?: string;
  address?: string;
  loading: boolean;
  reloading: boolean;
}> = ({ image, name, address, loading, reloading }) => {

  return (
    <div>
      {/* Skeleton loader for image if loading */}
      {loading || reloading ? (
        <Skeleton height={330} width="100%" className="rounded-xl" />
      ) : (
        <Image
          width={400}
          height={400}
          className="rounded-xl w-full object-cover max-h-[330px]"
          src={image as string}
          alt="branch image"
        />
      )}

      {/* Skeleton loader for text if loading */}
      <h3 className="text-secondary-main capitalize font-medium mt-4">
        {loading ? <Skeleton width={200} /> : name || "Not Available"}
      </h3>

      <h4 className="text-label-main h5 mb-6 flex gap-2">
        <span>
          <svg
            width="21"
            height="27"
            viewBox="0 0 21 27"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M10.1745 15.1381C12.0475 15.1381 13.5658 13.6197 13.5658 11.7468C13.5658 9.87381 12.0475 8.35547 10.1745 8.35547C8.30154 8.35547 6.7832 9.87381 6.7832 11.7468C6.7832 13.6197 8.30154 15.1381 10.1745 15.1381Z"
              stroke="#606060"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M10.1734 2.70178C7.77488 2.70178 5.47464 3.65457 3.77866 5.35056C2.08268 7.04654 1.12988 9.34678 1.12988 11.7453C1.12988 13.884 1.58432 15.2835 2.82553 16.8322L10.1734 25.3105L17.5212 16.8322C18.7624 15.2835 19.2168 13.884 19.2168 11.7453C19.2168 9.34678 18.264 7.04654 16.5681 5.35056C14.8721 3.65457 12.5718 2.70178 10.1734 2.70178Z"
              stroke="#606060"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {loading ? (
          <Skeleton width={150} />
        ) : (
          <span className="capitalize">{address || "Not available"}</span>
        )}
      </h4>
    </div>
  );
};

export interface BranchData {
  title: string;
  values: Record<string, number | string>;
}
type BranchSummaryCardProps = BranchSummary & {
  loading?: boolean;
  className?: string;
  titleAs?: "h1" | "h2" | "h3" | "h4" | "h5" | "h6"; // Union type for heading tags
};

export const BranchSummaryCard: React.FC<BranchSummaryCardProps> = ({
  title,
  loading,
  noHeader,
  recent,
  className,
  values = {}, // ✅ Ensures values is at least an empty object
  variant,

  second,
  titleAs = "h4", // Default to "h4" if no titleAs is passed
}) => {
  const formatTitle = (key: string): string =>
    key.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());

  const bannedValues = ["0", 0, "₦0", null, "null", "$0", "0.00", "€0", "£0"];
  const filteredValues = Object.entries(values).filter(
    ([, value]) => !bannedValues.includes(value)
  );
  const TitleTag: React.ElementType = titleAs; // Dynamically set the tag based on titleAs
  return (
    <div
      className={`${
        noHeader && "pt-0"
      } shadow-md ${className} bg-white rounded-xl p-4 w-full h-max`}
    >
      <TitleTag
        className={`mb-1 h5 !font-bold ${noHeader && "hidden"} pb-3 ${
          recent && "!border-none"
        } text-secondary-main border-b border-b-disabled-main/50 ${
          second && "!border-b-themeColor"
        }`}
      >
        {title}
      </TitleTag>
      {loading ? (
        <Skeleton className="my-1" height={30} count={12} />
      ) : filteredValues.length > 0 ? (
        <div
          className={`divide-y-[2px] divide-gray-300 ${
            second && "divide-themeColor"
          }`}
        >
          {filteredValues.map(([key, value]) => (
            <div
              key={key}
              className={`flex items-center ${
                variant ? "gap-4" : "justify-between items-center"
              } py-4`}
            >
              <span className="font-medium text-lg text-secondary-main">
                {formatTitle(key)}:
              </span>
              <span
                className={recent ? "font-semibold capitalize " : "capitalize"}
              >
                {value}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-gray-500 italic py-3">No data available</div> // ✅ Handles empty values gracefully
      )}
    </div>
  );
};

// Example Usage
export const branchData: BranchData = {
  title: "Branch Summary",
  values: {
    total_staff: 100,
    property_for_rent: 100,
    property_for_sale: 100,
    property_for_short_let: 100,
    total_unit_managing: 100,
    total_reviews: 100,
    completed_transaction: 100,
  },
};

const SecondCol: React.FC<{ branchDetails: BranchType; loading: boolean }> = ({
  branchDetails,
  loading,
}) => {

  return (
    <BranchSummaryCard loading={loading} {...branchDetails?.branchSummary} />
  );
};

const FourthCol: React.FC<{ data?: MemberType[]; loading: boolean }> = ({
  data,
  loading,
}) => {
  const [selectedRole, setSelectedRole] = useState<string>("all");

  // extract unique roles
  const roles = useMemo(() => {
    if (!data) return [];
    const unique = Array.from(new Set(data.map((m) => m.role)));
    return unique;
  }, [data]);

  // filter data
  const filteredData = useMemo(() => {
    if (!data) return [];
    if (selectedRole === "all") return data;
    return data.filter((m) => m.role === selectedRole);
  }, [data, selectedRole]);

  const options = [
    { value: "all", label: "All Roles" },
    ...roles.map((r) => ({ value: r, label: r })),
  ];

  return (
    <div className="py-8 ">
      <div className="item flex justify-between items-center">
        <h2 className="font-semibold">Branch Staffs</h2>

        {roles.length > 0 && (
          <div className="filter rounded-md h-max p-2 border border-themeColor">
            <CustomSelect
              options={options}
              value={selectedRole}
              onChange={(val) => setSelectedRole(val)}
              placeholder="Select role"
              align="right"
            />
          </div>
        )}
      </div>

      {loading ? (
        <CardGrid gapY={150} gap={30} maxColumns={3}>
          {Array.from({ length: 6 }).map((_, index) => (
            <PropertyCardSkeleton key={index} />
          ))}
        </CardGrid>
      ) : filteredData.length > 0 ? (
        <CardGrid className="mt-[8rem]" gapY={150} gap={30} maxColumns={3}>
          {filteredData.map((item) => (
            <TeamCard className="h-full" key={item.id} {...item} />
          ))}
        </CardGrid>
      ) : (
        <div>No staff information attached yet. Please try again later.</div>
      )}
    </div>
  );
};
